BookingService.$inject = ['_', 'Sports', 'Time', 'CONST', 'moment'];

/* @ngInject */
function BookingService(_, Sports, Time, CONST, moment) {
    /**
     * @class Booking
     * @constructor
     * @param  {object} data
     * @param  {moment} date
     */
    function Booking(data, date) {
        this.loaded = false;
        this.bookeable = null;
        this.done = null;
        this.creator = null;
        this.attrs = {};
        this.note = new Note();
        this.deleted = false;

        this.payment = null;

        if (!data) {
            return this;
        }

        this.load(data);
    }

    //24 hours before and after
    Booking.REPORT_START = -1440;
    Booking.REPORT_END = 1440;
    Booking.CANCEL_END = 60 * 12;
    Booking.CASHOUT_BEFORE = 1;
    Booking.CASHOUT_SHOULD = 15; // minutes after finished for suggesting cashout

    /**
     * @method api
     * @return {Restangular}
     */
    Booking.prototype.api = function () {
        return this.court.api.one('bookings', this.id);
    };

    /**
     * @method load
     * @param  {object} data
     * @chainable
     */
    Booking.prototype.load = function (data) {
        // Read only attributes
        this.id = data.id;

        this.bookeable = data.bookeable || {};
        this.last_done = data.last_done || {};
        this.done = data.booking_done || null;
        this.creator = data.creator || null;
        this.loaded = true;

        this.attrs.type_metadata = data.type_metadata || null;
        this.attrs.duration = this.bookeable.duration;
        this.attrs.online = data.online;
        this.attrs.sport = Sports.get(data.sport_id);
        this.attrs.created = data.created_at; // DEPRECATED
        this.attrs.created_at = data.created_at;
        this.attrs.created_by = data.created_by;
        this.attrs.spetial_type = data.type;
        this.attrs.fixed = Boolean(data.fixed);
        this.attrs.blocker = Boolean(data.blocker);
        this.attrs.state = data.state;

        this.payment = data.payment;

        _.assign(this.attrs, this.startEnd());

        this.update(data);

        return this;
    };

    /**
     * @method update
     * @param  {object} data
     * @chainable
     */
    Booking.prototype.update = function (data) {
        this.bookeable = data.bookeable || this.bookeable;
        this.done = data.booking_done || this.done;
        this.creator = data.creator || this.creator;

        this.note.set(data.note);
        this.attrs.discount = data.discount;
        this.attrs.price = data.price;
        this.attrs.balance = data.balance;
        this.attrs.balance_abs = Math.abs(data.balance);
        this.attrs.user = data.user;
        this.attrs.updated = data.updated_at;

        this.isDone();

        return this;
    };

    /**
     * @method refresh
     * @return {$q}
     */
    Booking.prototype.refresh = function () {
        var self = this;
        return this.api()
            .get()
            .then(function (booking) {
                booking = booking.plain();
                if (!self.loaded) {
                    self.load(booking);
                } else {
                    self.update(booking);
                }
                return booking;
            });
    };

    /**
     * @method setCourt
     * @param  {Court} court
     * @chainable
     */
    Booking.prototype.setCourt = function (court) {
        this.court = court;
        return this;
    };
    /**
     * @method getCourt
     * @return {Court}
     */
    Booking.prototype.getCourt = function () {
        return this.court;
    };

    /**
     * @method isFixed
     * @return {Boolean}
     */
    Booking.prototype.isFixed = function () {
        return this.attrs.fixed;
    };

    /**
     * @method isOnline
     * @return {Boolean}
     */
    Booking.prototype.isOnline = function () {
        return this.attrs.online;
    };

    /**
     * @method isMatch
     * @return {Boolean}
     */
    Booking.prototype.isMatch = function () {
        return this.attrs.type_metadata?.source == 'matches';
    };

    /**
     * @method getGender
     * @return {Srting}
     */
    Booking.prototype.getGender = function () {
        const gender_allowed = this.attrs.type_metadata?.gender_allowed;

        if (gender_allowed == 'Male') return 'M';
        if (gender_allowed == 'Female') return 'F';
        if (gender_allowed == 'Mixed') return 'MX';
        return 'SR';
    };

    /**
     * @method getCategory
     * @return {Srting}
     */
    Booking.prototype.getCategory = function () {
        const formatLevel = (number) => {
            const suffixes = [
                'ra',
                'da',
                'ra',
                'ta',
                'ta',
                'ta',
                'ma',
                'va',
                'na',
                'ma',
            ];

            const lastDigit = number % 10;
            const suffix = suffixes[lastDigit - 1] || 'ra';

            return `${number}${suffix}`;
        };

        const min = this.attrs.type_metadata?.level_min;
        const max = this.attrs.type_metadata?.level_max;

        const formattedMin = formatLevel(min);
        const formattedMax = formatLevel(max);

        if (max === min) return formattedMin;
        if (max - min === 1) return `${formattedMin} / ${formattedMax}`;

        return `${formattedMin} a ${formattedMax}`;
    };

    /**
     * @method getType
     * @return {Object}
     */
    Booking.prototype.getType = function () {
        return CONST.bookings.types[this.attrs.spetial_type];
    };

    /**
     * @method getUser
     * @return {Object}
     */
    Booking.prototype.getUser = function () {
        return this.attrs.user;
    };

    /**
     * @method getCreator
     * @return {Object}
     */
    Booking.prototype.getCreator = function () {
        if (this.creator) {
            return this.creator;
        }

        if (_.isObject(this.attrs.created_by)) {
            return this.attrs.created_by;
        }

        return null;
    };

    /**
     * @method isCancelled
     * @return {Boolean}
     */
    Booking.prototype.isCancelled = function () {
        return (
            (this.done && this.done.cancelled && this.done.done_at) ||
            (this.bookeable && this.bookeable.cancelled)
        );
    };

    /**
     * @method isDone
     * @return {Boolean}
     */
    Booking.prototype.isDone = function () {
        if (this.done && this.done.done_at) {
            this.attrs.isDone = !this.done.cancelled;
        } else {
            this.attrs.isDone = undefined;
        }
        return this.attrs.isDone;
    };

    /**
     * @method isBloquer
     * @return {Boolean}
     */
    Booking.prototype.isBlocker = function () {
        return Boolean(this.attrs.blocker);
    };

    /**
     * @method isPending
     * @return {Boolean}
     */
    Booking.prototype.isPending = function () {
        return this.attrs.state === 'pending';
    };

    /**
     * @method isReady
     * @return {Boolean}
     */
    Booking.prototype.isReady = function () {
        return !this.isBlocker() && !this.isPending();
    };

    /**
     * @method getSport
     * @return {Sport}
     */
    Booking.prototype.getSport = function () {
        return this.attrs.sport;
    };

    /**
     * @method startEnd
     * @return {object}
     * @return {Hour} return.start
     * @return {Hour} return.end
     */
    Booking.prototype.startEnd = function () {
        var _date = this.isDone()
            ? this.done.datetime
            : this.bookeable.datetime_start;
        var start = new Time.Hour().fromMoment(_date);
        return {
            start: start,
            end: Time.durationToEnd(start, this.bookeable.duration),
        };
    };

    /**
     * @method getDate
     * @return {Moment}
     */
    Booking.prototype.getDate = function () {
        return Time.moment(this.bookeable.datetime_start);
    };

    /**
     * @method getDateString
     * @return {String}
     */
    Booking.prototype.getDateString = function () {
        return this.getDate().format('dddd DD [de] MMMM HH:mm[hs]');
    };

    /**
     * @method getPrice
     * @return {int}
     */
    Booking.prototype.getPrice = function (dow) {
        if (_.isNull(this.attrs.price) || _.isUndefined(this.attrs.price)) {
            return this.court.price(
                dow !== undefined ? dow : '*',
                this.attrs.start.toFloat(),
                this.attrs.duration,
            );
        }
        return this.attrs.price;
    };

    /**
     * @method getCreated
     * @return {Moment}
     * @deprecated Now using getCreatedAt
     */
    Booking.prototype.getCreated = function () {
        return this.getCreatedAt();
    };

    /**
     * @method getCreated
     * @return {Moment}
     */
    Booking.prototype.getCreatedAt = function () {
        return Time.moment(this.attrs.created_at);
    };

    Booking.prototype.getDone = function () {
        return this.done;
    };

    Booking.prototype.isPlaying = function (date) {
        var diff = Time.moment().diff(date, 'minutes');
        return diff > 0 && diff < this.attrs.duration;
    };

    Booking.prototype.hasPassed = function (date) {
        var diff = Time.moment().diff(date, 'minutes');
        return diff > this.attrs.duration;
    };

    Booking.prototype.canCashout = function (date) {
        if (this.isDone() || this.isCancelled()) {
            return false;
        }
        var d = Time.moment(date).subtract(1, 'minutes');
        return Time.moment()
            .subtract(Booking.CASHOUT_BEFORE, 'minutes')
            .isAfter(d);
    };

    Booking.prototype.shouldCashout = function () {
        if (this.isDone()) {
            return false;
        }

        var end = this.startEnd().end.toMoment();

        var cashoutTime = end.add(Booking.CASHOUT_SHOULD, 'minutes');

        return Time.moment().isAfter(cashoutTime);
    };

    Booking.prototype.canCancel = function (date) {
        if (this.isDone() || this.isCancelled()) {
            return false;
        }

        
     

        if (this.attrs.fixed) {
            const currentDate = Time.moment().format('YYYY-MM-DD');
            const bookingSince = this.getSinceDate().format('YYYY-MM-DD');

            const is_booking_since_in_the_future = bookingSince > currentDate;

            //Solo puedo cancelar el primer booking de un turno fijo que aún no arranco (<= 1 y no 0 porque el since en la DB se guarda un dia antes)
            if (is_booking_since_in_the_future) {
                return Time.moment(date).diff(bookingSince, 'days') <= 1;
            }
    
            
            const endDate = Time.moment(date).add(this.attrs.duration, 'minutes');
            
            const diffInDays = Time.moment(endDate).diff(Time.moment(), 'days');
            

            if (diffInDays >= 7) return false;
        }

        if (
            Time.moment(date).format('DD-MM') ===
            Time.moment().subtract(1, 'day').format('DD-MM')
        ) {
            return true;
        }
        return Time.moment()
            .subtract(Booking.CANCEL_END, 'minutes')
            .isSameOrBefore(date);
    };

    Booking.prototype.canReport = function () {
        if (!this.isOnline()) {
            return false;
        }

        if (this.done) return false;

        var date = this.getDate(),
            diff = Time.moment().diff(date, 'minutes');

        return diff <= Booking.REPORT_END && diff >= Booking.REPORT_START;
    };

    Booking.prototype.isSameDay = function (date) {
        return Time.isSame(date, this.getDate());
    };

    /*
     * Payment
     */

    Booking.prototype.hasPayment = function () {
        return CONST.online_payment && Boolean(this.payment);
    };

    Booking.prototype.getRemaining = function () {
        if (!this.hasPayment()) {
            return this.getPrice() - this.attrs.balance;
        }

        if (this.payment.isCapture) {
            return this.getPrice() - (this.payment.amount + (this.attrs.balance - this.payment.amount));
            // return this.getPrice() - this.payment.amount;
        } else {
            return this.getPrice() - this.attrs.balance;
        }
    };

    Booking.prototype.getPartialPayment = function () {
        if (this.hasPayment() && this.payment.isCapture) {
            return this.attrs.balance - this.payment.amount;
        }
        return this.attrs.balance;
    };

    Booking.prototype.getAdvance = function () {
        if (this.hasPayment()) {
            return this.payment.amount;
        }
        return this.attrs.balance;
    };

    Booking.prototype.getAdvanceAbs = function () {
        return Math.abs(this.getAdvance());
    };

    Booking.prototype.getPayedAdvance = function () {
        if (this.hasPayment()) {
            if (this.payment.isCapture) {
                return this.payment.amount + this.getPartialPayment();
                // return this.payment.amount;
            } else {
                return 0 + this.getPartialPayment();
            }
        }
        return this.attrs.balance;
    };

    /*******/
    Booking.prototype.getWeeksToPlay = function () {
        var since = this.getSinceDate();
        var end = this.getEndDate();

        if (since && end) {
            since = since.clone().startOf('day');
            end = end.clone().startOf('day');
            return end.diff(since, 'weeks') + 1;
        }
        return 0;
    };

    Booking.prototype.getSinceDate = function (formated) {
        var aux = moment(this.bookeable.since);

        return aux.isValid()
            ? formated
                ? aux.startOf('day').format('DD-MM-YYYY')
                : aux.startOf('day')
            : null;
    };

    Booking.prototype.getEndDate = function (formated) {
        var aux = moment(this.bookeable.end);

        return aux.isValid()
            ? formated
                ? aux.startOf('day').format('DD-MM-YYYY')
                : aux.startOf('day')
            : null;
    };

    /*******/

    /**
     * @class BookingFixed
     * @extends {Booking}
     * @constructor
     */
    function BookingFixed() {
        Booking.apply(this, arguments);
    }

    BookingFixed.prototype = _.create(Booking.prototype, {
        constructor: BookingFixed,
    });

    BookingFixed.prototype.startEnd = function () {
        var bookeable = this.bookeable,
            start = new Time.Hour().fromFloat(bookeable.time);
        return {
            start: start,
            end: Time.durationToEnd(start, bookeable.duration),
        };
    };

    BookingFixed.prototype.isFixed = function () {
        return true;
    };

    BookingFixed.prototype.getDate = function () {
        return parseInt(this.bookeable.day);
    };

    BookingFixed.prototype.getDateString = function () {
        var day = this.getDate(),
            hour = this.startEnd().start;

        if (hour.hour > 24) {
            hour.hour = hour.hour % 24;
            day = (day + 1) % 7;
        }

        return Time.moment.weekdays()[day] + ' ' + hour.toString() + 'hs';
    };

    BookingFixed.prototype.getWeeksToPlay = function () {
        var end = this.getEndDate();
        var since = moment().startOf('day');

        if (end) {
            end = end.clone().startOf('day');
            return end.diff(since, 'weeks') + 1;
        }
        return 0;
    };

    BookingFixed.prototype.getLastBookingDate = function (formated) {
        var weeks = this.getWeeksToPlay();
        var since = this.getSinceDate();

        if (since) {
            since.add(weeks, 'weeks');
            return formated ? since.format('DD-MM-YYYY') : since;
        }
        return 'Not since date';
    };

    /*BookingFixed.prototype.getSinceDate = function () {
        var aux = moment(this.bookeable.since);

        return aux.isValid() ? aux : null;
    }
    BookingFixed.prototype.getEndDate = function () {
        var aux = moment(this.bookeable.end);

        return aux.isValid() ? aux : null;
    }*/

    BookingFixed.prototype.isSameDay = function (date) {
        return Time.momentToDay(date) === this.getDate();
    };

    /**
     * Note class
     * @class Note
     * @constructor
     * @param  {String|null} note
     */
    function Note(note) {
        this.value = note;
    }

    Note.prototype.has = function () {
        return Boolean(this.value);
    };

    Note.prototype.get = function () {
        return this.value;
    };

    Note.prototype.getText = function () {
        return this.value && this.value.text;
    };

    Note.prototype.set = function (text, by, date) {
        if (_.isObject(text)) {
            this.value = text;
            return this;
        }

        if (text === null) {
            return this.clear();
        }

        this.value = {
            text: text,
            by: by,
            date: date,
        };
        return this;
    };

    Note.prototype.clear = function () {
        this.value = null;
        return this;
    };

    return {
        Booking: Booking,
        BookingFixed: BookingFixed,
    };
}

export default BookingService;
